import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

// Hooks
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import { UPDATE_POST_TYPES, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';

// Lib
import { INSTAGRAM, PINTEREST, TWITTER } from '@/lib/socialProfile';
import { postsSortTypes, storiesSortTypes } from '@/lib/posts/postSortTypes';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

// Components
import Icon from '@/components/base/Icon';
import Select from '@/components/base/Select';

const ButtonVariant = {
    default: 'text-icoBlack',
    light: 'text-icoGray-400 text-sm'
};

type ButtonSelectPostTypeProps = Required<Omit<SelectPostTypeProps, 'initialOptions'>> & {
    error?: string;
    disabled?: boolean;
    selected?: SelectPostOptionsType;
};

const ButtonSelectPostType = ({
    error,
    disabled,
    selected,
    variant = 'default',
    leadingIcon
}: ButtonSelectPostTypeProps) => {
    const { t } = useTranslation('content');
    const { activeSocialProfile } = useActiveSocialProfile();
    const network: SocialProfileNetworkType = activeSocialProfile?.social_network_type;
    const { albumInfo } = useAlbumContext();

    function getLabel() {
        switch (network) {
            case INSTAGRAM:
                if (albumInfo?.criteria?.album_type === 'stories') {
                    return {
                        iconLabel: 'story',
                        textLabel: 'content:modal.story_type'
                    };
                }
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.post_type'
                };
            case PINTEREST:
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.pin_type'
                };
            case TWITTER:
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.tweet_type'
                };
            default:
                return {
                    iconLabel: 'content',
                    textLabel: 'content:modal.post_type'
                };
        }
    }

    const { iconLabel, textLabel } = getLabel();

    const labels = useMemo(
        () => selected.map((type) => type.label).join(', '),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selected]
    );

    return (
        <Select.Button
            selected={selected}
            disabled={disabled}
            baseStyle={variant === 'default' ? 'noBorder' : 'grayBackground'}
            error={error}>
            <span className="flex items-center space-x-3">
                {leadingIcon ? (
                    <Icon
                        icon={iconLabel}
                        className="text-base text-icoGray-400 dark:text-icoDarkMode-wolf"
                    />
                ) : null}
                <span className={clsx('block truncate dark:text-white', ButtonVariant[variant])}>
                    {`${t(textLabel)}: ${labels}`}
                </span>
            </span>
        </Select.Button>
    );
};

type SelectPostOptionsType = ArrayElement<
    ValueOfObject<ReturnType<typeof postsSortTypes>> | ReturnType<typeof storiesSortTypes>
>[];

type SelectPostTypeProps = {
    leadingIcon?: boolean;
    variant?: 'default' | 'auto';
    initialOptions?: SelectPostOptionsType;
};

const SelectPostType = ({
    leadingIcon = true,
    variant = 'default',
    initialOptions
}: SelectPostTypeProps) => {
    const { t } = useTranslation('commons');
    const { albumInfo, dispatch } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const albumType: 'stories' = albumInfo?.criteria?.album_type;
    const network: SocialProfileNetworkType = activeSocialProfile?.social_network_type;
    const postTypes: SelectPostOptionsType =
        albumType === 'stories' ? storiesSortTypes(t) : postsSortTypes(t)[network];
    const fallbackOptions = [postTypes[0]];
    const [selected, setSelected] = useState<SelectPostOptionsType>(
        initialOptions?.map((option) => {
            return { ...option, label: t(option.label) };
        }) ?? fallbackOptions
    );

    useEffect(() => {
        dispatch({
            type: UPDATE_POST_TYPES,
            payload: selected[0].value === 'all' ? [] : selected.map((item) => item.value)
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    useEffect(() => {
        if (!albumInfo?.criteria?.post_types.length && selected[0].value !== 'all') {
            setSelected(fallbackOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [albumInfo?.criteria?.post_types]);

    const isSelected = (item: ArrayElement<SelectPostOptionsType>['value']) => {
        return !!selected.find((el) => el.value === item);
    };

    function handleSelected(item: SelectPostOptionsType) {
        let result = fallbackOptions;
        if (item.length > 0) {
            const newValue = item[item.length - 1].value;
            if (newValue !== 'all') {
                result = item.filter((val) => val.value !== 'all');
                if (isSelected(newValue)) {
                    // Deselect
                    result = item.filter((val) => val.value !== newValue);

                    if (result.length === 0) {
                        result = fallbackOptions;
                    }
                }
            }
        }

        setSelected(result);
    }

    return (
        <Select
            data-testid="select-post-type"
            selected={selected}
            setSelected={handleSelected}
            options={postTypes}
            noBorder
            button={<ButtonSelectPostType leadingIcon={leadingIcon} variant={variant} />}
            isMultiple={true}
        />
    );
};

export default SelectPostType;
